import moment, {DurationInputArg1} from 'moment';
import _ from 'lodash';
import {CreditCard} from './models/CreditCard';
import Settings from './Settings';
import {Platform} from 'react-native';
import {Balance} from './models/Balance';
import * as RNLocalize from 'react-native-localize';
import {Location} from 'src/types/serverTypes/Location';
import LocationFeatures, {Sfecfg} from 'src/models/LocationFeatures';
import AccountStore from 'src/stores/AccountStore';
import {store} from 'src/redux/store';
import {fetchAccount, fetchHomeLocation} from 'src/redux/slices/accountSlice';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {useCallback, useContext, useEffect, useState} from 'react';
import LocationHandler from 'src/handlers/LocationHandler';
import {
  fetchCampusLocations,
  fetchNearbyCampusesAndLocations,
} from 'src/redux/slices/campusLocationSlice';
import MainConsumerContext from 'src/components/MainConsumerContext';
import ActionsFactory from 'src/actions/ActionsFactory';
import MenuService from 'src/services/MenuService';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import orderAheadUUID from 'src/nativeModules/UUID';
import Events from 'src/logging/Events';
import {alertError} from 'src/components/helpers/AlertHelper';
import Localized from 'src/constants/AppStrings';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import {BalanceTypes} from 'src/types/serverTypes/Account';
import AccountConstants from 'src/constants/AccountConstants';
import UUID from 'src/nativeModules/UUID';

type FormatOptionsType = {
  maximumFractionDigits: number;
  minimumFractionDigits: number;
};
type DateTimeObj = {
  date: string;
  time: string;
};
interface BalanceContext {
  homeLocationId: string;
  userOrgId: string;
}

export function delay(interval: number) {
  return new Promise<void>((fulfill: any) => {
    setTimeout(fulfill, interval);
  });
}
export async function delayForUX(started: any) {
  const futureTime = moment().add(10, 'seconds');
  const delayTime = futureTime.diff(moment()); // Get the difference in milliseconds

  await delay(delayTime);
}
export function numberWithCommas(x: number | string) {
  const parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}
export function formatEpochDate(epoch: number) {
  const options: any = {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  };
  return new Date(epoch).toLocaleDateString([], options);
}
export function formatEpochDateTime(epoch: number) {
  const options: any = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };
  return new Date(epoch).toLocaleDateString([], options);
}

export function formatAddress({street, city, state, zip}) {
  const line1 = street?.trim() ?? '';

  const cityStateZip = [city, state, zip]
    .filter((part) => part && part.trim().length > 0)
    .join(' ');

  if (line1 && cityStateZip) {
    return `${line1} ${cityStateZip}`;
  } else if (line1) {
    return line1;
  } else {
    return cityStateZip;
  }
}

export function retrieveDate(
  subtractDuration: DurationInputArg1,
  formatString = 'YYYY-MM-DD',
) {
  return moment().subtract(subtractDuration).format(formatString);
}
export function getCurrentDate(format = 'YYYY-MM-DD HH:mm:ss') {
  return moment().format(format);
}
export function getCurrentTimeMillis() {
  return moment().valueOf();
}

export function calculateDaysInMillis(days: number) {
  const now = moment().add(days, 'd').valueOf();
  return now;
}

export function getCurrentUTCDate(format = 'YYYY-MM-DD HH:mm:ss') {
  const localDate = moment().format(format);
  const utcDate = moment(localDate, format).utc();
  return utcDate.format(format);
}
export function formatDate(
  date: Date | string | number,
  format = 'YYYY-MM-DD HH:mm',
) {
  return moment(date).format(format);
}
export function getDurationFromDate(date: string) {
  const now = moment();
  const sentDate = moment(date);
  const years = now.diff(sentDate, 'years');

  if (years > 0) {
    return years + 'y';
  }

  const days = now.subtract(years, 'years').diff(sentDate, 'days');

  if (days > 0) {
    return days + 'd';
  }

  const hours = now.subtract(days, 'days').diff(sentDate, 'hours');

  if (hours > 0) {
    return hours + 'h';
  }

  const minutes = now.subtract(hours, 'hours').diff(sentDate, 'minutes');

  if (minutes > 0) {
    return minutes + 'm';
  }

  return '1m';
}

export function isDatePastNinetyDays(date: string) {
  const ninetyDaysAgo = moment().subtract({
    days: 90,
  });
  return moment(date).isBefore(ninetyDaysAgo);
}

export function guid() {
  let d = new Date().getTime();

  if (
    window &&
    window.performance &&
    typeof window.performance.now === 'function'
  ) {
    d += window.performance.now(); // use high-precision timer if available
  }

  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    // eslint-disable-next-line no-bitwise
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
}
export function formatPercent(numerator: number, denominator: number) {
  const percent = Math.floor((numerator / denominator) * 100);
  return `${percent}%`;
}
export function formatNumber(
  context: any,
  value: number,
  options: FormatOptionsType = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  },
): string {
  if (!context || !context.globalize) {
    return value.toString();
  }

  const formatter = context.globalize.getNumberFormatter(options);
  return typeof value === 'number' ? formatter(value) : '';
}
export function formatCurrency(
  context: any,
  value: number,
  currency: string,
  options: FormatOptionsType = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  },
): string {
  if (!context || !context.globalize) {
    return '$' + value.toFixed(2).toString();
  }

  const currencyFormatter = context.globalize.getCurrencyFormatter(
    currency,
    options,
  );
  return currencyFormatter(
    typeof value === 'string' ? parseFloat(value) : value,
  );
}
export function onlyNumbers(text: string) {
  return text.replace(/\D/g, '');
}
export function isOnlyNumbers(text: string) {
  return /^\d+$/.test(text);
}
export function hashCode(toHash: string) {
  let hash = 0;

  if (toHash.length === 0) {
    return hash;
  }

  for (let i = 0; i < toHash.length; i++) {
    const char = toHash.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + char;
    // eslint-disable-next-line no-bitwise
    hash = hash & hash; // Convert to 32bit integer
  }

  return hash;
}
export function random(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min) + min);
}
export function parseDateTime(datetime: string): DateTimeObj {
  return {
    date: datetime.substring(0, 10),
    time: datetime.substring(11),
  };
}
export function getLast4(ccNumber: string): string {
  if (ccNumber && ccNumber.length > 4) {
    return ccNumber.substring(ccNumber.length - 4);
  }

  return ccNumber;
}
export function changeExtension(fileName: string, extension: string): string {
  const pos = fileName.lastIndexOf('.');
  return `${fileName.substr(0, pos < 0 ? fileName.length : pos)}.${extension}`;
}
export function getLocationAddress(location: any) {
  let address = '';

  if (location.address) {
    address = location.address;
  }

  if (location.city) {
    if (address) {
      address += ', ';
    }

    address += location.city;
  }

  if (location.state) {
    if (address) {
      address += ', ';
    }

    address += location.state;
  }

  return address;
}
export function parseUrlParams(url: string): Record<string, string> {
  const params: Record<string, string> = {};

  if (url) {
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    let match = regex.exec(url);

    while (match) {
      params[match[1]] = match[2];
      match = regex.exec(url);
    }
  }

  return params;
}
export function getTransactionCode(transactionId: string) {
  return transactionId?.slice(-6) ?? '';
}
export function getAmountInCents(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100);
}
export function roundNumber(amount: number, numDigits: number): number {
  const adjustment1: any = 'e+' + numDigits;
  const adjustment2 = 'e-' + numDigits;
  return +(Math.round(amount + adjustment1) + adjustment2);
}
export function isCollectionEqual(x: any, y: any) {
  return _(x).differenceWith(y, _.isEqual).isEmpty();
}

// checks if val is null, undefined, empty or not a number
export function checkValue(val?: null | number | '') {
  let canBeUsed = true;
  if (val === null || val === undefined || val === '' || isNaN(val || 0)) {
    canBeUsed = false;
  }
  return canBeUsed;
}

// Checks the navigation state to determine the screen the user has come from
export function getPreviousRouteName(
  routes: Array<{key: string; name: string; params?: any; path?: string}>,
): string {
  if (routes === null || routes === undefined || routes.length === 0) {
    return '';
  } else if (routes.length === 1) {
    return '';
  }
  const previousRoute = routes[routes.length - 2];
  return previousRoute.name;
}

export function addLeadingZero(value: string, length: number): string {
  return value.padStart(length, '0');
}

export function filterExpiredCreditCards(
  creditCards: Array<CreditCard>,
): Array<CreditCard> {
  const currentMonth = moment().format('MM');
  const currentYear = moment().format('YYYY');
  const creditCardsFiltered = creditCards.filter(
    (c) =>
      c.expirationYear > +currentYear ||
      (c.expirationYear === +currentYear && c.expirationMonth >= +currentMonth),
  );
  return creditCardsFiltered;
}

export function isCreditCardExpired(creditCard: CreditCard): boolean {
  const currentMonth = moment().format('MM');
  const currentYear = moment().format('YYYY');
  return (
    creditCard.expirationYear < +currentYear ||
    (creditCard.expirationYear === +currentYear &&
      creditCard.expirationMonth < +currentMonth)
  );
}

export function getAppOsName(): string {
  return Settings.appDisplayName + '-' + Platform.OS;
}

export function capitalizeFirstWord(str: string): string {
  let words = str.split(/\s+/);
  words = words.map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word.toLowerCase();
  });
  return words.join(' ');
}

export function getArticle(word) {
  const trimmedWord = word.trim().toLowerCase();
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const firstChar = trimmedWord.charAt(0);

  // Check if the first character is a vowel
  if (vowels.includes(firstChar)) {
    return 'an';
  } else {
    return 'a';
  }
}
export function checkIS365() {
  return Settings.isRefiveAnd365Pay() ? '#066DFF' : null;
}

export function secondLetterCapitalize(str: string) {
  const deviceLanguage = RNLocalize.getLocales()[0]?.languageCode;

  if (deviceLanguage !== 'en') {
    return str;
  }

  const newArr = str.split(' ');
  newArr[1] = newArr[1].charAt(0).toLowerCase() + newArr[1].slice(1);
  return newArr.join(' ');
}

export function getClosestValue(input: number, array) {
  const tempArray = _.sortBy(array);
  const index = tempArray.sort().findIndex((item) => {
    return input < item;
  });
  if (index >= 0) {
    return tempArray[index];
  } else {
    return null; // no answer
  }
}

export function getTotalDisplayAmount(balances: Balance[] = []) {
  if (!balances || balances.length === 0) {
    return 0;
  }
  return balances
    .filter(
      (item) =>
        (item.isDefault || item.isSubsidy || item.isSMA) && !item.isDisabled,
    )
    .reduce((sum, item) => sum + item.balance, 0);
}

export function removeTrailingEscapeSequencesAndSpaces(inputString) {
  const trailingPattern = /\\[nrtbfv0'"\x\u]?|\s$/;
  return inputString.replace(trailingPattern, '');
}

async function validateHomeLocationDetails(isSma: boolean, orgId: string) {
  let updatedHomeOrgId = orgId;
  let updatedIsSMA = isSma;
  const account = await store
    .dispatch(fetchAccount(AccountStore.getAccountId()))
    .unwrap();
  updatedHomeOrgId = account.account.orgId;
  const location = await store
    .dispatch(fetchHomeLocation(account.account.locationId))
    .unwrap();
  const sfecfg = new LocationFeatures(location.locationFeatures as Sfecfg);
  updatedIsSMA = sfecfg.IsSma;
  return {updatedHomeOrgId, updatedIsSMA};
}

export async function filterLocationsPerBalanceType(
  isSMA: boolean,
  homeOrgId: string,
  locations: Location[],
) {
  const supportedLocations = [];
  const unSupportedLocations = [];
  let homeOrg = homeOrgId;
  let isSMAHome = isSMA;
  if (!homeOrgId) {
    const val = await validateHomeLocationDetails(isSMA, homeOrgId);
    homeOrg = val.updatedHomeOrgId;
    isSMAHome = val.updatedIsSMA;
  }
  locations.map((loc) => {
    const locationFeature = new LocationFeatures(
      loc.locationFeatures as Sfecfg,
    );
    if (homeOrg === null) {
      supportedLocations.push(loc);
      return;
    }
    if (isSMAHome && locationFeature.IsSma) {
      if (homeOrg === loc.orgId) {
        supportedLocations.push(loc);
      } else {
        unSupportedLocations.push(loc);
      }
      return;
    } else if (!isSMAHome && !locationFeature.IsSma) {
      supportedLocations.push(loc);
      return;
    }
    unSupportedLocations.push(loc);
    return;
  });
  return {
    supportedLocations,
    unSupportedLocations,
  };
}

export async function filterVendingLocationsPerBalanceType(
  isSMA: boolean,
  homeOrgId: string,
  vendingLocations: Location[],
) {
  let isSMAHome = isSMA;
  let homeOrg = homeOrgId;
  if (!homeOrgId) {
    const value = await validateHomeLocationDetails(isSMA, homeOrgId);
    homeOrg = value.updatedHomeOrgId;
    isSMAHome = value.updatedIsSMA;
  }
  if (homeOrg === null || !isSMAHome) {
    return {
      supportedVendingLocations: vendingLocations,
      unSupportedVendingLocations: [],
    };
  } else {
    return {
      supportedVendingLocations: [],
      unSupportedVendingLocations: vendingLocations,
    };
  }
}

export async function validateGiftCardForSma(
  isSMA: boolean,
  homeOrgId: string,
  giftOrgId: string | null,
): Promise<boolean> {
  let isSMAHome = isSMA;
  let homeOrg = homeOrgId;
  if (!homeOrgId) {
    const value = await validateHomeLocationDetails(isSMA, homeOrgId);
    homeOrg = value.updatedHomeOrgId;
    isSMAHome = value.updatedIsSMA;
  }
  if ((homeOrg === null || (homeOrg && !isSMAHome)) && giftOrgId === null) {
    return true;
  } else if (isSMAHome && homeOrg === giftOrgId) {
    return true;
  }
  return false;
}
export const useNearbyLocations = () => {
  const dispatch = useAppDispatch();
  const nearbyLocations = useAppSelector(
    (s) => s.campusLocation.nearbyCampusesAndLocations,
  );
  const recentLocations = useAppSelector(
    (s) => s.campusLocation.recentCampusesAndLocations,
  );

  useEffect(() => {
    const fetchNearby = async () => {
      await LocationHandler.getCurrentLocation();
      const position = LocationHandler.currentLocation;
      dispatch(
        fetchNearbyCampusesAndLocations({
          lat: position?.coords?.latitude,
          lon: position?.coords?.longitude,
        }),
      );
    };
    fetchNearby();
  }, [dispatch]);

  const locationData = [
    ...(nearbyLocations || []),
    ...(recentLocations || []).filter(
      (recent) =>
        !(nearbyLocations || []).some(
          (nearby) => nearby.locationId === recent.locationId,
        ),
    ),
  ];

  return locationData;
};
export const useSelectedLocation = (initialLocations) => {
  const dispatch = useAppDispatch();
  const [selectedLocation, setSelectedLocation] = useState(
    initialLocations?.length === 1 ? initialLocations[0] : null,
  );

  const selectLocation = useCallback(
    async (location) => {
      setSelectedLocation(location);
      if (location?.locationId) {
        await dispatch(fetchCampusLocations(location.locationId));
      }
    },
    [dispatch],
  );

  return {
    selectedLocation,
    selectLocation,
  };
};
export const useOrderAhead = () => {
  const context = useContext(MainConsumerContext);

  const handleOrderAhead = useCallback(
    async (machine, autoAdvanced = false, getRouteFromLocationConfig) => {
      try {
        context?.actions.showSpinner();

        const locationResponse =
          await ActionsFactory.getAccountActions().getLocation(
            machine.locationId,
          );
        MenuService.setTimezone(locationResponse.timezone);

        const param = {
          autoAdvanced,
          location: {...machine, ...locationResponse},
        };

        if (locationResponse.onlineOrderConfig.disclaimer) {
          NavActions.push(AppRoutes.Disclaimer, {
            ...param,
            getRouteFromPickupLocations: getRouteFromLocationConfig,
          });
        } else {
          const routeDetails = await getRouteFromLocationConfig(
            locationResponse,
            autoAdvanced,
          );
          if (!routeDetails.error) {
            NavActions.push(
              routeDetails.name,
              {
                ...param,
                selectedPickupLocation: routeDetails.selectedPickupLocation,
                availableTimes: routeDetails.availableTimes,
              },
              'machines-pickup',
            );
          }
        }
      } catch (error) {
        const guid = await orderAheadUUID.getRandomUUID();
        CrashlyticsEvents.log(
          'Exception',
          'HandleOrderAhead',
          generateErrorMessage(error),
          guid,
        );
        Events.Error.trackEvent(
          'Exception',
          'HandleOrderAhead',
          generateErrorMessage(error),
          guid,
        );
        alertError(Localized.Errors.order_ahead_unavailable, guid);
      } finally {
        context?.actions.hideSpinner();
      }
    },
    [context],
  );

  return {
    handleOrderAhead,
  };
};

export function getTotalAvailableBalance(balances: Balance[] = []): number {
  if (balances.length === 0) {
    return 0;
  }

  const ctx: BalanceContext = {
    homeLocationId: AccountStore.getLocationId(),
    userOrgId: AccountStore.getOrg(),
  };

  const main = balances.find(balanceIsMain(ctx));
  if (!main) {
    return 0;
  }

  const subsidyTotal = balances
    .filter(balanceCanSubsidise(main, ctx))
    .reduce((sum, b) => sum + b.balance, 0);

  return main.balance + subsidyTotal;
}

function balanceIsMain(ctx: Pick<BalanceContext, 'homeLocationId'>) {
  return (b: Balance): boolean => {
    if (b.isDisabled) {
      return false;
    }

    return (
      b.isDefault ||
      b.type === BalanceTypes.Stripe ||
      (b.isSMA && b.locationId === ctx.homeLocationId)
    );
  };
}

function balanceCanSubsidise(main: Balance, ctx: BalanceContext) {
  return (b: Balance): boolean => {
    if (!b.isSubsidy || b.isDisabled || b.currency !== main.currency) {
      return false;
    }

    if (main.type === BalanceTypes.Stripe && b.orgId !== ctx.userOrgId) {
      return false;
    }

    if (main.type === BalanceTypes.Sma && b.locationId !== ctx.homeLocationId) {
      return false;
    }

    return true;
  };
}

const baseTypeArray = [
  AccountConstants.SALE_TYPE,
  AccountConstants.FUNDING_TYPE,
  AccountConstants.REFERRAL_SIGNUP_TYPE,
  AccountConstants.REFERRAL_TYPE,
  AccountConstants.REFUND_TYPE,
  AccountConstants.REDEMPTION_TYPE,
  AccountConstants.SEND_SNACK,
  AccountConstants.SEND_SNACK_CANCEL,
  AccountConstants.SEND_SNACK_REVERSE,
  AccountConstants.SEND_SNACK_ACCEPT,
  AccountConstants.SEND_SNACK_EXPIRE,
];
export const buildTypeArray = (type, transactionTypeDisplay) => {
  const shouldIncludeAdjustment =
    type === AccountConstants.ADJUSTMENT_TYPE &&
    transactionTypeDisplay === AccountConstants.PROMO_CREDIT;

  const arrayToUse = [...baseTypeArray];

  if (shouldIncludeAdjustment) {
    arrayToUse.push(AccountConstants.ADJUSTMENT_TYPE);
  }

  return arrayToUse;
};
export const getSenderTitle = (status) => {
  return ['e', 'c'].includes(status ? status.toLowerCase() : '')
    ? Localized.Labels.my_account
    : Localized.Labels.your_snack_sent;
};
export const getTransactionDate = (deal) => {
  return deal?.transDate || deal?.endDate;
};
export const rewardsStartPolicyInBackground = async (
  acctId: string,
  policyId?: string,
  source?: string,
) => {
  const rewardProgramId = AccountStore.getRewardStatus()?.rewardProgramId;
  try {
    await ActionsFactory.getAccountActions().startPolicyAction(
      acctId,
      rewardProgramId,
      policyId,
    );
  } catch (error) {
    const guids = await UUID.getRandomUUID();
    const context = `${source}:startPolicyAction`;
    const message = generateErrorMessage(error);

    CrashlyticsEvents.log('Error', context, message, guids);
    Events.Error.trackEvent('Error', context, message, guids);
  }
};
export const rewardsCancelPolicyInBackground = async (
  acctId: string,
  policyId?: string,
  source?: string,
) => {
  const rewardProgramId = AccountStore.getRewardStatus()?.rewardProgramId;
  try {
    await ActionsFactory.getAccountActions().cancelPolicyAction(
      acctId,
      rewardProgramId,
      policyId,
    );
  } catch (error) {
    const guids = await UUID.getRandomUUID();
    const context = `${source}:cancelPolicyAction`;
    const message = generateErrorMessage(error);

    CrashlyticsEvents.log('Error', context, message, guids);
    Events.Error.trackEvent('Error', context, message, guids);
  }
};
export const submitAppSurveyInBackground = async (
  acctId: string,
  rewardProgramPolicyId: string,
  responses: {question: string; answer: string}[],
  title: string,
  source?: string,
) => {
  try {
    await ActionsFactory.getAccountActions().submitAppSurveyAction(
      acctId,
      rewardProgramPolicyId,
      responses,
      title,
    );
  } catch (error) {
    const guids = await UUID.getRandomUUID();
    const context = `${source}:submitAppSurveyAction`;
    const message = generateErrorMessage(error);

    CrashlyticsEvents.log('Error', context, message, guids);
    Events.Error.trackEvent('Error', context, message, guids);
  }
};

export const addSlash = (prev: string, next: string): string => {
  const digitsOnly = next.replace(/\D/g, '');
  if (digitsOnly.length < 3) {
    return digitsOnly;
  }
  const month = digitsOnly.substring(0, 2);
  const year = digitsOnly.substring(2);
  return `${month}/${year}`;
};

export const isValidMonthYear = (input: string): boolean =>
  /^(0[1-9]|1[0-2])\/\d{4}$/.test(input);

export const isValidYear = (input: string, yearsBack: number): boolean => {
  const [mStr, yStr] = input.split('/');
  const month = Number(mStr);
  const year = Number(yStr);
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 1 - yearsBack;
  return !(year >= currentYear || month > 12 || year <= startYear);
};

export const validateMonthYearInput = (
  val: string,
  yearsBack: number,
  localized?: {
    birthMonthYearError: string;
    birthMonthYearErrorExceedsYear: string;
  },
): string => {
  if (!isValidMonthYear(val)) {
    return localized?.birthMonthYearError;
  }
  if (!isValidYear(val, yearsBack)) {
    return localized?.birthMonthYearErrorExceedsYear;
  }
  return '';
};

export const isDateExpired = (date: string, format = 'YYYY-MM-DD') => {
  const endOfDay = moment(date, format, true).endOf('day');
  if (!endOfDay.isValid()) {
    return false;
  }
  return moment().isAfter(endOfDay);
};

export const getNumberOfColumnsInGrid = (): number => {
  return Platform.OS === 'web' ? 4 : 2;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  const number = phoneNumber.trim().replace(/[^0-9]/g, '');
  if (number.length < 4) {
    return number;
  } else if (number.length < 7) {
    return number.replace(/(\d{3})(\d{1})/, '$1-$2');
  } else if (number.length < 11) {
    return number.replace(/(\d{3})(\d{3})(\d{1})/, '$1-$2-$3');
  }
  return number.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
};

export default {
  delay,
  numberWithCommas,
  formatEpochDate,
  formatEpochDateTime,
  retrieveDate,
  getCurrentDate,
  calculateDaysInMillis,
  getCurrentTimeMillis,
  getCurrentUTCDate,
  formatDate,
  guid,
  formatPercent,
  formatNumber,
  formatCurrency,
  onlyNumbers,
  isOnlyNumbers,
  hashCode,
  random,
  parseDateTime,
  getLast4,
  changeExtension,
  getLocationAddress,
  parseUrlParams,
  getTransactionCode,
  getDurationFromDate,
  isDatePastNinetyDays,
  delayForUX,
  getAmountInCents,
  roundNumber,
  isCollectionEqual,
  checkValue,
  getPreviousRouteName,
  addLeadingZero,
  filterExpiredCreditCards,
  getAppOsName,
  capitalizeFirstWord,
  checkIS365,
  secondLetterCapitalize,
  isCreditCardExpired,
  removeTrailingEscapeSequencesAndSpaces,
  filterLocationsPerBalanceType,
  filterVendingLocationsPerBalanceType,
  validateGiftCardForSma,
  useNearbyLocations,
  useSelectedLocation,
  useOrderAhead,
  getTotalAvailableBalance,
  isDateExpired,
  getNumberOfColumnsInGrid,
  formatPhoneNumber,
};
